import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import MainLayout from "../modules/layout/main-layout";
import UserLayoutDynamic from "../modules/layout/user-layout/user-layout-dynamic";
import DSSLoader from "../ui/building-blocks/atoms/dss-loader/dss-loader";
import React, { Suspense, useEffect } from "react";
import { SessionProvider, useSession } from "../auth-provider/providers/session-provider/session-provider";
import SessionAlert from "../auth-provider/components/SessionAlert/SessionAlert";
import { useDssContext } from "../DSSContext";
import { MODULE_BASE_PATHS } from "../modules/dashboard/side-bars/sidebar-user/sidebar-user-routes";

const OverviewPage = React.lazy(() => import("../modules/inds-pre-login-screen/pages/overview-page"));
const ProductAndServices = React.lazy(() => import("../modules/inds-pre-login-screen/component/support/product-n-services"));
const CustomerSupport = React.lazy(() => import("../modules/inds-pre-login-screen/component/support/customer-support"));
const OnlineHelp = React.lazy(() => import("../modules/inds-pre-login-screen/component/support/online-help"));
const QualityDocuments = React.lazy(() => import("../modules/inds-pre-login-screen/component/support/quality-documents"));
const ToolsTechInfo = React.lazy(() => import("../modules/inds-pre-login-screen/component/support/tools-n-tech-info"));
const NewsEvents = React.lazy(() => import("../modules/inds-pre-login-screen/component/support/news-events"));
const DownloadModule = React.lazy(() => import("../modules/download/route"));
const AlertsAndNotices = React.lazy(() => import("../modules/inds-pre-login-screen/component/flight-info/alerts-and-notices"));
const EGPWSterDbSearch = React.lazy(() => import("../modules/inds-pre-login-screen/component/flight-info/egpws-terdb-search"));
const LatestAllUpdates = React.lazy(() => import("../modules/inds-pre-login-screen/component/overview/event-sections/latest-update/latest-all-updates").then(module => ({ default: module.LatestAllUpdates })));
const DataOrMediaType = React.lazy(() => import("../modules/inds-pre-login-screen/component/flight-info/database-mediatype/database-mediatype").then(module => ({ default: module.DataOrMediaType })));
const DistributionModule = React.lazy(() => import("../modules/distribution/route"));
const SubcriptionModule = React.lazy(() => import("../modules/subscriptions/route"));
const MyAccountModule = React.lazy(() => import("../modules/my-account/route"));
const UserCareModule = React.lazy(() => import("../modules/user-care/route"));

const CustomerRouterContent = () => {
  const location = useLocation();
  const { startTimer, stopTimer } = useSession();
  const { allowedRoutes, allowedRoutesReady } = useDssContext();

  useEffect(() => {
    startTimer();
    return () => { stopTimer(); };
  }, [startTimer, stopTimer]);

  const isRouteAllowed = (currentPath: string): boolean => {
    if (currentPath === '/userlogin/overview' || currentPath === '/userlogin' || currentPath === '/userlogin/') return true;

    const exactOrChild = allowedRoutes.some(route =>
      currentPath === route || currentPath.startsWith(route + '/')
    );
    if (exactOrChild) return true;

    const isModuleBase = MODULE_BASE_PATHS.some(base =>
      currentPath === base || currentPath === base + '/'
    );
    if (isModuleBase) {
      return allowedRoutes.some(route =>
        route.startsWith(currentPath.replace(/\/$/, '') + '/') ||
        route.startsWith(currentPath.replace(/\/$/, ''))
      );
    }

    return false;
  };

  if (allowedRoutesReady && !isRouteAllowed(location.pathname)) {
    return <Navigate to="/userlogin/overview" replace />;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <MainLayout>
            <UserLayoutDynamic />
          </MainLayout>
        }
      >
        <Route
          path="/"
          element={<Navigate to="overview" />}
        />
        <Route
          path={`overview/*`}
          element={
            <Suspense fallback={<DSSLoader id={""} />}>
              <OverviewPage />
            </Suspense>
          }
        />
        <Route
          path={`overview/allUpdates`}
          element={
            <Suspense fallback={<DSSLoader id={""} />}>
              <LatestAllUpdates />
            </Suspense>
          }
        />
        <Route
          path={`/support/*`}
          element={
            <Suspense fallback={<DSSLoader id={""} />}>
              <ProductAndServices />
            </Suspense>
          }
        />
        <Route
          path={`/support/customerSupport`}
          element={
            <Suspense fallback={<DSSLoader id={""} />}>
              <CustomerSupport />
            </Suspense>
          }
        />
        <Route
          path={`/support/onlineHelp`}
          element={
            <Suspense fallback={<DSSLoader id={""} />}>
              <OnlineHelp />
            </Suspense>
          }
        />
        <Route
          path={`/support/qualityDocuments`}
          element={
            <Suspense fallback={<DSSLoader id={""} />}>
              <QualityDocuments />
            </Suspense>
          }
        />
        <Route
          path={`/support/toolsInfo`}
          element={
            <Suspense fallback={<DSSLoader id={""} />}>
              <ToolsTechInfo />
            </Suspense>
          }
        />
        <Route
          path={`/support/newsEvents`}
          element={
            <Suspense fallback={<DSSLoader id={""} />}>
              <NewsEvents />
            </Suspense>
          }
        />
        <Route
          path={`/database/*`}

          element={
            <Suspense fallback={<DSSLoader id={""} />}>
              <DownloadModule />
            </Suspense>
          }
        />
        <Route
          path={`/distribution/*`}

          element={
            <Suspense fallback={<DSSLoader id={""} />}>
              <DistributionModule />
            </Suspense>
          }
        />
        <Route
          path={`/subscription/*`}

          element={
            <Suspense fallback={<DSSLoader id={""} />}>
              <SubcriptionModule />
            </Suspense>
          }
        />
        <Route
          path={`/my-account/*`}

          element={
            <Suspense fallback={<DSSLoader id={""} />}>
              <MyAccountModule />
            </Suspense>
          }
        />
        <Route
          path={`/user-care/*`}

          element={
            <Suspense fallback={<DSSLoader id={""} />}>
              <UserCareModule />
            </Suspense>
          }
        />
        <Route
          path={`/flight-info/*`}
          element={
            <Suspense fallback={<DSSLoader id={""} />}>
              <AlertsAndNotices />
            </Suspense>
          }
        />
        <Route
          path={`/flight-info/egpws-search`}
          element={
            <Suspense fallback={<DSSLoader id={""} />}>
              <EGPWSterDbSearch />
            </Suspense>
          }
        />
        <Route
          path={`/flight-info/database-mediatype`}
          element={
            <Suspense fallback={<DSSLoader id={""} />}>
              <DataOrMediaType />
            </Suspense>
          }
        />
      </Route>
    </Routes>
  );
};

const CustomerRouter = () => {
  return (
    <SessionProvider>
      <SessionAlert />
      <CustomerRouterContent />
    </SessionProvider>
  );
};

export default CustomerRouter;